import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  Divider,
  Button,
  useTheme,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Folder,
  AttachMoney,
  Assignment,
  CalendarToday,
  MoreVert,
  Add,
  Visibility,
  CheckCircle,
  Warning,
  Description,
  Gavel,
  Person,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

// API fetch functions
const fetchDashboardStats = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/stats', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch dashboard stats');
  return response.json();
};

const fetchRevenueChart = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/revenue-chart', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch revenue data');
  return response.json();
};

const fetchCaseStatusChart = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/case-status-chart', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch case status data');
  return response.json();
};

const fetchRecentActivity = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/recent-activity?limit=6', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch recent activity');
  return response.json();
};

const fetchUpcomingEvents = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/upcoming-events?limit=5', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch upcoming events');
  return response.json();
};

const fetchUpcomingTasks = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/dashboard/upcoming-tasks?limit=5', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch upcoming tasks');
  return response.json();
};

// Hoisted to module scope on purpose. Declared inside DashboardPage it was a
// new component type on every render, so React remounted each card instead of
// updating it, restarting the skeleton and hover transitions.
const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  loading?: boolean;
  onClick?: () => void;
}> = ({ title, value, icon, color, trend, loading, onClick }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: 4,
      } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={40} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend.startsWith('+') ? (
                  <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                ) : trend.startsWith('-') ? (
                  <TrendingDown sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                ) : null}
                <Typography 
                  variant="caption" 
                  color={trend.startsWith('+') ? 'success.main' : trend.startsWith('-') ? 'error.main' : 'text.secondary'}
                >
                  {trend} this month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              backgroundColor: color,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      )}
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch all dashboard data — tenantId in keys blocks cross-tenant cache reuse
  const { data: statsData, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats', user?.tenantId],
    queryFn: fetchDashboardStats,
    refetchInterval: 60000, // Refresh every minute
    enabled: Boolean(user?.tenantId),
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['dashboard-revenue', user?.tenantId],
    queryFn: fetchRevenueChart,
    refetchInterval: 60000,
    enabled: Boolean(user?.tenantId),
  });

  const { data: caseStatusData, isLoading: caseStatusLoading } = useQuery({
    queryKey: ['dashboard-case-status', user?.tenantId],
    queryFn: fetchCaseStatusChart,
    refetchInterval: 60000,
    enabled: Boolean(user?.tenantId),
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity', user?.tenantId],
    queryFn: fetchRecentActivity,
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: Boolean(user?.tenantId),
  });

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['dashboard-events', user?.tenantId],
    queryFn: fetchUpcomingEvents,
    refetchInterval: 60000,
    enabled: Boolean(user?.tenantId),
  });

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['dashboard-tasks', user?.tenantId],
    queryFn: fetchUpcomingTasks,
    refetchInterval: 60000,
    enabled: Boolean(user?.tenantId),
  });

  const stats = statsData?.data || {};
  const revenueChartData = revenueData?.data || [];
  const caseStatusChartData = caseStatusData?.data || [];
  const recentActivity = activityData?.data || [];
  const upcomingEvents = eventsData?.data || [];
  const upcomingTasks = tasksData?.data || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return 'success';
      case 'new':
        return 'primary';
      case 'pending':
      case 'in_progress':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'case':
        return <Gavel fontSize="small" />;
      case 'client':
        return <Person fontSize="small" />;
      case 'document':
        return <Description fontSize="small" />;
      case 'task':
        return <Assignment fontSize="small" />;
      default:
        return <Visibility fontSize="small" />;
    }
  };

  return (
    <Box>
      {statsError && (
        <ListLoadErrorAlert
          resourceLabel="dashboard data"
          message="Some dashboard metrics couldn’t be refreshed. Layout and shortcuts below still work."
          onRetry={() => refetchStats()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your legal practice.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Cases"
            value={stats.activeCases || 0}
            icon={<Folder />}
            color={theme.palette.primary.main}
            trend={stats.caseGrowth}
            loading={statsLoading}
            onClick={() => navigate('/cases')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clients"
            value={stats.totalClients || 0}
            icon={<People />}
            color={theme.palette.secondary.main}
            trend={stats.newClientsThisMonth > 0 ? `+${stats.newClientsThisMonth} new` : undefined}
            loading={statsLoading}
            onClick={() => navigate('/clients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue"
            value={`$${(stats.revenueThisMonth || 0).toLocaleString()}`}
            icon={<AttachMoney />}
            color={theme.palette.success.main}
            loading={statsLoading}
            onClick={() => navigate('/billing')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Tasks"
            value={stats.pendingTasks || 0}
            icon={<Assignment />}
            color={stats.overdueTasks > 0 ? theme.palette.error.main : theme.palette.warning.main}
            trend={stats.overdueTasks > 0 ? `${stats.overdueTasks} overdue` : undefined}
            loading={statsLoading}
            onClick={() => navigate('/tasks')}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader
              title="Revenue Overview"
              subheader="Monthly revenue trends (last 6 months)"
              action={
                <IconButton onClick={() => navigate('/reports')}>
                  <MoreVert />
                </IconButton>
              }
            />
            <CardContent>
              {revenueLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : revenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
                      labelFormatter={(label) => revenueChartData.find((d: any) => d.month === label)?.fullMonth || label}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.light}
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No revenue data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Case Status Chart */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Case Status"
              subheader="Distribution by status"
            />
            <CardContent>
              {caseStatusLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : caseStatusChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={caseStatusChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {caseStatusChartData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No cases to display</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity, Upcoming Events & Tasks */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Activity"
              action={
                <Button size="small" startIcon={<Add />} onClick={() => navigate('/cases')}>
                  View All
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {activityLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  ))}
                </Box>
              ) : recentActivity.length > 0 ? (
                <List disablePadding>
                  {recentActivity.map((activity: any, index: number) => (
                    <React.Fragment key={activity.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: `${getStatusColor(activity.status)}.main`,
                            width: 36,
                            height: 36,
                          }}
                        >
                          {getActivityIcon(activity.type)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {activity.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {activity.description}
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={activity.status}
                              size="small"
                              color={getStatusColor(activity.status) as any}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {activity.timeAgo}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recent activity
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Events */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Upcoming Events"
              action={
                <Button size="small" startIcon={<CalendarToday />} onClick={() => navigate('/calendar')}>
                  Calendar
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {eventsLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  ))}
                </Box>
              ) : upcomingEvents.length > 0 ? (
                <List disablePadding>
                  {upcomingEvents.map((event: any, index: number) => (
                    <React.Fragment key={event.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 36, height: 36 }}>
                          <CalendarToday fontSize="small" />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {event.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {event.date} at {event.time}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={event.type.replace('_', ' ')}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Box>
                      </ListItem>
                      {index < upcomingEvents.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No upcoming events
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Tasks */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Priority Tasks"
              action={
                <Button size="small" startIcon={<Assignment />} onClick={() => navigate('/tasks')}>
                  View All
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {tasksLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                    </Box>
                  ))}
                </Box>
              ) : upcomingTasks.length > 0 ? (
                <List disablePadding>
                  {upcomingTasks.map((task: any, index: number) => (
                    <React.Fragment key={task.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: task.isOverdue ? 'error.main' : 
                                    task.priority === 'urgent' ? 'error.main' :
                                    task.priority === 'high' ? 'warning.main' : 'info.main',
                            width: 36,
                            height: 36,
                          }}
                        >
                          {task.isOverdue ? <Warning fontSize="small" /> : <Assignment fontSize="small" />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {task.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {task.dueDate ? `Due: ${task.dueDate}` : 'No due date'}
                            {task.assignedTo && ` • ${task.assignedTo}`}
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                            <Chip
                              label={task.priority}
                              size="small"
                              color={
                                task.priority === 'urgent' ? 'error' :
                                task.priority === 'high' ? 'warning' : 'default'
                              }
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {task.isOverdue && (
                              <Chip
                                label="Overdue"
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      {index < upcomingTasks.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No pending tasks
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
